import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import api from "services/api"
import { ICartPresubscription } from "./types"

export function useCart(
  memberId?: string,
  config?: UseQueryOptions<ICartPresubscription[]>
) {
  return useQuery<ICartPresubscription[]>(
    [
      "operation",
      "administration",
      "members",
      "portal",
      memberId,
      "presubscriptions",
      "cart",
    ],
    {
      enabled: !!memberId,
      ...config,
    }
  )
}

export function useAddToCart(
  memberId: string,
  config?: UseMutationOptions<
    unknown,
    unknown,
    { events: string[]; apply_to_similar_events: boolean }
  >
) {
  const queryClient = useQueryClient()
  return useMutation<
    unknown,
    unknown,
    { events: string[]; apply_to_similar_events: boolean }
  >(
    (data) =>
      api.post(
        `operation/administration/members/portal/${memberId}/presubscriptions/cart`,
        data
      ),
    {
      ...config,
      onSuccess(data, vars, context) {
        config?.onSuccess?.(data, vars, context)
        queryClient.invalidateQueries([
          "operation",
          "administration",
          "members",
          "portal",
          memberId,
          "presubscriptions",
          "cart",
        ])
        queryClient.invalidateQueries([
          "operation",
          "administration",
          "members",
          "portal",
          memberId,
          "to_subscribe_events",
        ])
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "external",
        ])
      },
    }
  )
}

export function useDeleteFromCart(
  memberId: string,
  config?: UseMutationOptions<
    unknown,
    unknown,
    { presubscription_id: string; apply_to_similar_events: boolean }
  >
) {
  const queryClient = useQueryClient()
  return useMutation<
    unknown,
    unknown,
    { presubscription_id: string; apply_to_similar_events: boolean }
  >(
    (data) =>
      api.delete(
        `operation/administration/members/portal/${memberId}/presubscriptions/cart`,
        { data }
      ),
    {
      ...config,
      onSuccess(data, vars, context) {
        config?.onSuccess?.(data, vars, context)
        queryClient.invalidateQueries([
          "operation",
          "administration",
          "members",
          "portal",
          memberId,
          "presubscriptions",
          "cart",
        ])
        queryClient.invalidateQueries([
          "operation",
          "administration",
          "members",
          "portal",
          memberId,
          "to_subscribe_events",
        ])
      },
    }
  )
}

export function useConfirmCart(memberId: string, config?: UseMutationOptions) {
  const queryClient = useQueryClient()
  return useMutation(
    () =>
      api.post(
        `operation/administration/members/portal/${memberId}/presubscriptions/cart/confirm`
      ),
    {
      ...config,
      onSuccess(data, vars, context) {
        config?.onSuccess?.(data, vars, context)
        queryClient.removeQueries([
          "operation",
          "administration",
          "members",
          "portal",
          memberId,
          "presubscriptions",
          "cart",
        ])
        queryClient.invalidateQueries([
          "operation",
          "administration",
          "members",
          "portal",
          memberId,
          "presubscriptions",
        ])
      },
    }
  )
}
